<template>
  <div class="video-wrapper">
    <v-container
      v-if="!userActivated"
      color="primary"
      @click="startStream"
      :disabled="!deviceReady"
      class="click"
    >
      <span v-if="deviceReady" class="headline">
        Device is ready. <br>Click here to start live stream.
      </span>
    </v-container>
    <video
      v-else
      ref='remoteVideo'
      id="received_video"
      height="100%"
      autoplay
    ></video>
  </div>
</template>

<script>
import io from 'socket.io-client';
import { idToken } from '@/utils/auth';

export default {
  name: 'DeviceLiveView',
  data() {
    return {
      mediaConstraints: {
        audio: true,
        video: {
          aspectRatio: {
            ideal: 1.333333,
          },
        },
      },
      peerConnection: null,
      /* socket.io */
      socket: null,
      targetUsername: null,
      deviceReady: false,
      userClicked: false,
    };
  },
  props: {
    userStarted: {
      type: Boolean,
      required: false,
    },
  },
  computed: {
    userActivated() {
      return (this.userStarted || this.userClicked);
    },
  },
  async mounted() {
    this.targetUsername = this.$route.params.id;
    this.connect();
  },
  beforeDestroy() {
    this.sendToServer({
      type: 'hang-up',
      target: this.targetUsername,
    });
    this.socket.disconnect();
  },
  methods: {
    startStream() {
      this.userClicked = true;
      this.invite();
    },
    /*
    * Called by the WebRTC layer to let us know when it's time to
    * begin, resume, or restart ICE negotiation.
    */
    async handleNegotiationNeededEvent() {
      this.log('*** Negotiation needed');
      try {
        this.log('---> Creating offer');
        const offer = await this.peerConnection.createOffer();

        // If the connection hasn't yet achieved the "stable" state,
        // return to the caller. Another negotiationneeded event
        // will be fired when the state stabilizes.
        if (this.peerConnection.signalingState !== 'stable') {
          this.log('     -- The connection isn\'t stable yet; postponing...');
          return;
        }

        // Establish the offer as the local peer's current
        // description.
        this.log('---> Setting local description to the offer');
        await this.peerConnection.setLocalDescription(offer);

        // Send the offer to the remote peer.
        this.log('---> Sending the offer to the remote peer');

        this.socket.emit('video-offer', JSON.stringify({
          target: this.targetUsername,
          sdp: this.peerConnection.localDescription,
        }), (clientConnected) => {
          if (!clientConnected) {
            alert('device not connected to socket server. Connect it before trying to start live stream and refresh this page');
          }
        });
      } catch (err) {
        this.log('*** The following error occurred while handling the negotiationneeded event:');
        this.reportError(err);
      }
    },
    /*
    * Called by the WebRTC layer when events occur on the media tracks
    * on our WebRTC call. This includes when streams are added to and
    * removed from the call.
    *
    * track events include the following fields:
    *
    * RTCRtpReceiver       receiver
    * MediaStreamTrack     track
    * MediaStream[]        streams
    * RTCRtpTransceiver    transceiver
    *
    * In our case, we're just taking the first stream found and attaching
    * it to the <video> element for incoming media.
    */
    handleTrackEvent(event) {
      this.log('*** Track event');
      console.dir(event.streams);
      console.dir(event.track);
      [this.$refs.remoteVideo.srcObject] = event.streams;
    },
    /*
    * Handles |icecandidate| events by forwarding the specified
    * ICE candidate (created by our local ICE agent) to the other
    * peer through the signaling server.
    */
    handleICECandidateEvent(event) {
      if (event.candidate) {
        this.log(`*** Outgoing ICE candidate: ${event.candidate.candidate}`);

        this.sendToServer({
          type: 'new-ice-candidate',
          target: this.targetUsername,
          candidate: event.candidate,
        });
      }
    },
    /*
    * Handle |iceconnectionstatechange| events. This will detect
    * when the ICE connection is closed, failed, or disconnected.
    *
    * This is called when the state of the ICE agent changes.
    */
    handleICEConnectionStateChangeEvent(/* event */) {
      this.log(`*** ICE connection state changed to ${this.peerConnection.iceConnectionState}`);

      // eslint-disable-next-line
      switch (this.peerConnection.iceConnectionState) {
        case 'closed':
        case 'failed':
        case 'disconnected':
          this.closeVideoCall();
          break;
      }
    },
    /*
    * Set up a |signalingstatechange| event handler. This will detect when
    * the signaling connection is closed.
    *
    * NOTE: This will actually move to the new RTCPeerConnectionState enum
    * returned in the property RTCPeerConnection.connectionState when
    * browsers catch up with the latest version of the specification!
    */
    handleSignalingStateChangeEvent(/* event */) {
      this.log(`*** WebRTC signaling state changed to: ${this.peerConnection.signalingState}`);
      // eslint-disable-next-line
      switch (this.peerConnection.signalingState) {
        case 'closed':
          this.closeVideoCall();
          break;
      }
    },
    /*
    * Handle the |icegatheringstatechange| event. This lets us know what the
    * ICE engine is currently working on: "new" means no networking has happened
    * yet, "gathering" means the ICE engine is currently gathering candidates,
    * and "complete" means gathering is complete. Note that the engine can
    * alternate between "gathering" and "complete" repeatedly as needs and
    * circumstances change.
    *
    * We don't need to do anything when this happens, but we log it to the
    * console so you can see what's going on when playing with the sample.
    */
    handleICEGatheringStateChangeEvent(/* event */) {
      this.log(`*** ICE gathering state changed to: ${this.peerConnection.iceGatheringState}`);
    },
    /*
    * Close the RTCPeerConnection and reset variables so that the user can
    * make or receive another call if they wish. This is called both
    * when the user hangs up, the other user hangs up, or if a connection
    * failure is detected.
    */
    closeVideoCall() {
      this.log('Closing the call');

      // Close the RTCPeerConnection
      if (this.peerConnection) {
        this.log('--> Closing the peer connection');

        // Disconnect all our event listeners; we don't want stray events
        // to interfere with the hangup while it's ongoing.
        this.peerConnection.ontrack = null;
        this.peerConnection.onnicecandidate = null;
        this.peerConnection.oniceconnectionstatechange = null;
        this.peerConnection.onsignalingstatechange = null;
        this.peerConnection.onicegatheringstatechange = null;
        this.peerConnection.onnotificationneeded = null;

        // Stop all transceivers on the connection
        /*
        this.peerConnection.getTransceivers().forEach((transceiver) => {
          // transceiver.stop();
        });
        */
        // Close the peer connection
        this.peerConnection.close();
        this.peerConnection = null;
      }

      // this.targetUsername = null;
    },
    /*
    * Handle the "hang-up" message, which is sent if the other peer
    * has hung up the call or otherwise disconnected.
    */
    handleHangUpMsg() {
      this.log('*** Received hang up notification from other peer');
      this.closeVideoCall();
    },
    /*
    * Hang up the call by closing our end of the connection, then
    * sending a "hang-up" message to the other peer (keep in mind that
    * the signaling is done on a different connection). This notifies
    * the other peer that the connection should be terminated and the UI
    * returned to the "no call in progress" state.
    */
    hangUpCall() {
      this.closeVideoCall();

      this.sendToServer({
        target: this.targetUsername,
        type: 'hang-up',
      });
    },
    async invite() {
      if (!this.peerConnection) {
        this.log(`Inviting user ${this.targetUsername}`);

        // Call createPeerConnection() to create the RTCPeerConnection.
        // When this returns, peerConnection is our RTCPeerConnection
        // and webcamStream is a stream coming from the camera. They are
        // not linked together in any way yet.

        this.log(`Setting up connection to invite user: ${this.targetUsername}`);
        this.createPeerConnection();
        try {
          this.log('addTransceivers');
          this.peerConnection.addTransceiver('video', { direction: 'recvonly' });
          this.peerConnection.addTransceiver('audio', { direction: 'recvonly' });
        } catch (err) {
          this.handleGetUserMediaError(err);
        }
      }
    },
    handleGetUserMediaError(err) {
      this.log('handleGetUserMediaError');
      this.reportError(err);
      switch (err.name) {
        case 'NotFoundError':
          alert('Unable to open your call because no camera and/or microphone'
          + ' were found.');
          break;
        case 'SecurityError':
        case 'PermissionDeniedError':
          // Do nothing; this is the same as the user canceling the call.
          break;
        default:
          alert(`Error opening your camera and/or microphone: ${err.message}`);
          break;
      }
    },

    /*
    * Accept an offer to video chat. We configure our local settings,
    * create our RTCPeerConnection, get and attach our local camera
    * stream, then create and send an answer to the caller.
    */
    async handleVideoOfferMsg(msg) {
      this.targetUsername = msg.source;
      this.log('handleVideoOfferMsg');
      if (!this.peerConnection) {
        this.createPeerConnection();
      }
      const desc = new RTCSessionDescription(msg.sdp);

      // If the connection isn't stable yet, wait for it...
      if (this.peerConnection.signalingState !== 'stable') {
        this.log('  - But the signaling state isn\'t stable, so triggering rollback');
        // Set the local and remove descriptions for rollback; don't proceed
        // until both return.
        await Promise.all([
          this.peerConnection.setLocalDescription({ type: 'rollback' }),
          this.peerConnection.setRemoteDescription(desc),
        ]);
        return;
      }

      this.log('  - Setting remote description');
      await this.peerConnection.setRemoteDescription(desc);

      this.log('---> Creating and sending answer to caller');

      await this.peerConnection.setLocalDescription(await this.peerConnection.createAnswer());

      this.sendToServer({
        target: this.targetUsername,
        type: 'video-answer',
        sdp: this.peerConnection.localDescription,
      });
    },

    /*
    * Responds to the "video-answer" message sent to the caller
    * once the callee has decided to accept our request to talk.
    */
    async handleVideoAnswerMsg(msg) {
      this.log('handleVideoAnswerMsg');
      this.log('*** Call recipient has accepted our call');
      // Configure the remote description, which is the SDP payload
      // in our "video-answer" message.
      const desc = new RTCSessionDescription(msg.sdp);
      await this.peerConnection.setRemoteDescription(desc).catch(this.reportError);
    },
    async handleNewICECandidateMsg(msg) {
      this.log('handleNewICECandidateMsg');
      this.log(msg);
      const candidate = new RTCIceCandidate(msg.candidate);

      this.log(`*** Adding received ICE candidate: ${JSON.stringify(candidate)}`);
      try {
        await this.peerConnection.addIceCandidate(candidate);
      } catch (err) {
        this.reportError(err);
      }
    },
    /*
    * Create the RTCPeerConnection which knows how to talk to our
    * selected STUN/TURN server and then uses getUserMedia() to find
    * our camera and microphone and add that stream to the connection for
    * use in our video call. Then we configure event handlers to get
    * needed notifications on the call.
    */
    async createPeerConnection() {
      this.log('Setting up a connection...');

      // Create an RTCPeerConnection which knows to use our chosen
      // STUN server.
      // Information about ICE servers - Use your own!
      // A TURN server
      this.peerConnection = new RTCPeerConnection({
        iceServers: [
          {
            urls: 'stun:stun.l.google.com:19302',
          },
        ],
      });

      // Set up event handlers for the ICE negotiation process.
      this.peerConnection.onicecandidate = this.handleICECandidateEvent;
      this.peerConnection.oniceconnectionstatechange = this.handleICEConnectionStateChangeEvent;
      this.peerConnection.onicegatheringstatechange = this.handleICEGatheringStateChangeEvent;
      this.peerConnection.onsignalingstatechange = this.handleSignalingStateChangeEvent;
      this.peerConnection.onnegotiationneeded = this.handleNegotiationNeededEvent;
      this.peerConnection.ontrack = this.handleTrackEvent;
    },
    reportError(errMessage) {
      console.error(`Error ${errMessage.name}: ${errMessage.message}`);
    },
    sendToServer(msg) {
      const msgJSON = JSON.stringify(msg);
      this.log(`Sending ${msg.type} message: ${msgJSON}`);
      this.socket.emit(msg.type, msgJSON);
    },
    log(text) {
      const time = new Date();
      console.log(`[${time.toLocaleTimeString()}] ${text}`);
    },
    log_error(text) {
      const time = new Date();
      console.trace(`[${time.toLocaleTimeString()} ${text}`);
    },

    async sendAuthenticationResponse() {
      const token = await idToken();
      this.socket.emit('authenticate', token, (authenticated) => {
        this.log(`socket authenticated: ${authenticated}`);
      });
    },
    connect() {
      this.log('Connecting to server...');
      this.socket = io(process.env.VUE_APP_SIGNALLING_SERVER, { path: '/rtc' });

      this.socket.on('connect', () => {
        this.log('socket connected...');
      });

      this.socket.on('error', (error) => {
        console.trace(error);
      });

      this.socket.on('authenticate', async () => {
        this.log('\'authenticate\' request received');
        await this.sendAuthenticationResponse();
      });

      this.socket.on('video-offer', (data, ack) => {
        const msg = JSON.parse(data);
        this.log(`'video-offer' received: ${data}`);
        this.handleVideoOfferMsg(msg);
        if (ack) {
          ack(true);
        }
      });

      this.socket.on('video-answer', (data, ack) => {
        const msg = JSON.parse(data);
        this.log(`'video-answer' received: ${data}`);
        this.handleVideoAnswerMsg(msg);
        if (ack) {
          ack(true);
        }
      });

      this.socket.on('new-ice-candidate', (data, ack) => {
        const msg = JSON.parse(data);
        this.log(`'new-ice-candidate' received: ${data}`);
        this.handleNewICECandidateMsg(msg);
        if (ack) {
          ack(true);
        }
      });

      this.socket.on('hang-up', (data, ack) => {
        this.log('\'hang-up\' received');
        this.handleHangUpMsg();
        if (ack) {
          ack(true);
        }
      });

      this.socket.on('device-authenticated', (id, ack) => {
        this.log(`'device-authenticated' received ${id}`);
        if (ack) {
          ack(true);
        }
      });

      this.socket.on('device-ready', (id, ack) => {
        this.log(`'device-ready' received ${id}`);
        if (id === this.targetUsername) {
          this.deviceReady = true;
          if (this.userActivated) {
            this.invite();
          }
        }
        if (ack) {
          ack(true);
        }
      });
      this.socket.on('device-disconnected', (id, ack) => {
        this.log(`'device-disconnected' received ${id}`);
        if (ack) {
          ack(true);
        }
      });
    },
  },
};
</script>
<style scoped>
.video-wrapper {
  width: 100%;
  height: 60vh;
  background-color: #f0f0f0;
  text-align: center;
}
.click {
  width: 100%;
  height: 60vh;
  background-color: #f0f0f0;
  text-align: center;
  margin: auto;
  cursor: pointer;
  border: #a0a0a0;
  border-style: dashed;
  border-width: 2px;
  border-radius: 10px;
  line-height: 60vh;
}
span {
  display: inline-block;
  vertical-align: middle;
  line-height: normal;
  cursor: inherit
}
</style>
